/**
 * Government Contracting hub (reference-only).
 *
 * Read-focused: solicitations (tap → detail), certifications (with expiring
 * warnings), awarded contracts, and compliance requirements, all with badges.
 *
 * LEGAL / SAFETY (CRITICAL): this module is reference-only. Solicitation "match"
 * scores are non-binding suggestions and live on the detail screen; BEAM never
 * bids or commits. Nothing here submits anything.
 *
 * NEVER imports @smartbox/* (mobile invariant 1). No raw style values.
 */

import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import {
  listSolicitations,
  listCertifications,
  listExpiringCertifications,
  listContracts,
  listComplianceRequirements,
  listRegistrations,
  listProposals,
  type SolicitationRow,
  type CertificationRow,
  type ContractRow,
  type ComplianceRequirementRow,
  type FederalRegistrationRow,
  type ProposalRow,
} from '../../src/api/gov';
import {
  Screen,
  Text,
  Badge,
  Card,
  Button,
  ListRow,
} from '../../src/components/ui';
import type { StatusKey } from '../../src/theme';
import { theme } from '../../src/theme';
import { extractErrorMessage, humanize, formatCurrency } from '../../src/lib/format';

// ---------------------------------------------------------------------------
// Local status → badge maps (statusMap.ts is shared; not modified)
// ---------------------------------------------------------------------------

function solicitationBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'bid':
    case 'submitted':
      return { key: 'sent', label: humanize(status) };
    case 'awarded':
      return { key: 'paid', label: 'Awarded' };
    case 'reviewing':
      return { key: 'inProgress', label: 'Reviewing' };
    case 'no_bid':
    case 'lost':
    case 'cancelled':
      return { key: 'draft', label: humanize(status) };
    case 'watching':
      return { key: 'sent', label: 'Watching' };
    default:
      return { key: 'inProgress', label: humanize(status) || 'Watching' };
  }
}

function certBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'certified':
      return { key: 'paid', label: 'Certified' };
    case 'in_progress':
    case 'prospective':
      return { key: 'inProgress', label: humanize(status) };
    case 'expired':
    case 'denied':
      return { key: 'overdue', label: humanize(status) };
    default:
      return { key: 'draft', label: humanize(status) || 'Unknown' };
  }
}

function contractBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'active':
    case 'in_progress':
      return { key: 'inProgress', label: humanize(status) };
    case 'completed':
    case 'closed':
      return { key: 'paid', label: humanize(status) };
    case 'terminated':
    case 'cancelled':
      return { key: 'overdue', label: humanize(status) };
    default:
      return { key: 'sent', label: humanize(status) || 'Awarded' };
  }
}

function registrationBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'active':
    case 'complete':
      return { key: 'paid', label: humanize(status) };
    case 'pending':
    case 'in_progress':
    case 'submitted':
      return { key: 'inProgress', label: humanize(status) };
    case 'expired':
    case 'inactive':
    case 'rejected':
      return { key: 'overdue', label: humanize(status) };
    default:
      return { key: 'draft', label: humanize(status) || 'Unknown' };
  }
}

function proposalBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'submitted':
    case 'sent':
      return { key: 'sent', label: humanize(status) };
    case 'awarded':
    case 'accepted':
      return { key: 'paid', label: humanize(status) };
    case 'draft':
    case 'in_progress':
      return { key: 'inProgress', label: humanize(status) };
    case 'rejected':
    case 'withdrawn':
      return { key: 'overdue', label: humanize(status) };
    default:
      return { key: 'draft', label: humanize(status) || 'Draft' };
  }
}

function complianceBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'met':
    case 'compliant':
    case 'complete':
      return { key: 'paid', label: humanize(status) };
    case 'in_progress':
    case 'pending':
      return { key: 'inProgress', label: humanize(status) };
    case 'overdue':
    case 'missing':
    case 'non_compliant':
      return { key: 'overdue', label: humanize(status) };
    default:
      return { key: 'draft', label: humanize(status) || 'Unknown' };
  }
}

export default function GovernmentScreen() {
  const router = useRouter();

  const solicitationsQuery = useQuery({
    queryKey: ['gov-solicitations'],
    queryFn: listSolicitations,
    staleTime: 30_000,
  });
  const certsQuery = useQuery({
    queryKey: ['gov-certifications'],
    queryFn: listCertifications,
    staleTime: 60_000,
  });
  const expiringCertsQuery = useQuery({
    queryKey: ['gov-certifications-expiring'],
    queryFn: listExpiringCertifications,
    staleTime: 60_000,
  });
  const contractsQuery = useQuery({
    queryKey: ['gov-contracts'],
    queryFn: listContracts,
    staleTime: 60_000,
  });
  const complianceQuery = useQuery({
    queryKey: ['gov-compliance'],
    queryFn: listComplianceRequirements,
    staleTime: 60_000,
  });
  const registrationsQuery = useQuery({
    queryKey: ['gov-registrations'],
    queryFn: listRegistrations,
    staleTime: 60_000,
  });
  const proposalsQuery = useQuery({
    queryKey: ['gov-proposals'],
    queryFn: listProposals,
    staleTime: 60_000,
  });

  if (solicitationsQuery.isLoading) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <ActivityIndicator size="large" color={theme.color.action.primary} accessibilityLabel="Loading government data" />
          <Text variant="body" color="secondary">
            Loading…
          </Text>
        </View>
      </Screen>
    );
  }

  const solicitations = solicitationsQuery.data ?? [];
  const certs = certsQuery.data ?? [];
  const expiringIds = new Set((expiringCertsQuery.data ?? []).map((c) => c.id));
  const contracts = contractsQuery.data ?? [];
  const compliance = complianceQuery.data ?? [];
  const registrations = registrationsQuery.data ?? [];
  const proposals = proposalsQuery.data ?? [];

  return (
    <Screen scroll>
      <Text variant="title">Government contracting</Text>

      <Card>
        <Text variant="small" color="secondary">
          Reference and tracking only. Match scores are non-binding suggestions and
          BEAM never bids for you. You record your own bid / no-bid decisions and
          submit any proposals yourself.
        </Text>
      </Card>

      <Button
        title="Add / configure"
        onPress={() => router.push('/(app)/government/add')}
        accessibilityLabel="Add a solicitation, certification, contract, or compliance requirement"
      />

      {/* ------------------------------------------------------------------ */}
      {/* Solicitations */}
      {/* ------------------------------------------------------------------ */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Solicitations
        </Text>
        {solicitationsQuery.isError ? (
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(solicitationsQuery.error, 'Failed to load solicitations.')}
            </Text>
            <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => solicitationsQuery.refetch()} />
          </Card>
        ) : solicitations.length === 0 ? (
          <Card>
            <Text variant="body" color="secondary">
              No solicitations available.
            </Text>
          </Card>
        ) : (
          <View style={styles.list}>
            {solicitations.map((sol: SolicitationRow) => {
              const b = solicitationBadge(sol.status);
              const subtitle = [
                sol.solicitation_number ? `#${sol.solicitation_number}` : null,
                sol.issuing_agency,
                sol.response_due ? `Due ${new Date(sol.response_due).toLocaleDateString()}` : null,
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <ListRow
                  key={sol.id}
                  title={sol.title}
                  subtitle={subtitle || humanize(sol.status)}
                  right={<Badge status={b.key} label={b.label} />}
                  onPress={() => router.push(`/(app)/government/${sol.id}`)}
                />
              );
            })}
          </View>
        )}
      </View>

      {/* ------------------------------------------------------------------ */}
      {/* Federal registrations (SAM / UEI / CAGE) */}
      {/* ------------------------------------------------------------------ */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Federal registrations
        </Text>
        {registrationsQuery.isError ? (
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(registrationsQuery.error, 'Failed to load federal registrations.')}
            </Text>
            <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => registrationsQuery.refetch()} />
          </Card>
        ) : registrations.length === 0 ? (
          <Card>
            <Text variant="body" color="secondary">
              No federal registrations on file.
            </Text>
          </Card>
        ) : (
          <View style={styles.list}>
            {registrations.map((reg: FederalRegistrationRow) => {
              const b = registrationBadge(reg.registration_status);
              const subtitle = [
                reg.cage_code ? `CAGE ${reg.cage_code}` : null,
                reg.primary_naics ? `NAICS ${reg.primary_naics}` : null,
                reg.expiration_date ? `Expires ${new Date(reg.expiration_date).toLocaleDateString()}` : null,
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <ListRow
                  key={reg.id}
                  title={reg.uei ? `UEI ${reg.uei}` : 'Registration'}
                  subtitle={subtitle || humanize(reg.registration_status)}
                  right={<Badge status={b.key} label={b.label} />}
                  onPress={() => router.push(`/(app)/government/registration/${reg.id}`)}
                  accessibilityLabel={`Edit registration ${reg.uei ?? reg.id.slice(0, 8)}`}
                />
              );
            })}
          </View>
        )}
      </View>

      {/* ------------------------------------------------------------------ */}
      {/* Certifications */}
      {/* ------------------------------------------------------------------ */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Certifications
        </Text>
        {certsQuery.isError ? (
          <Card>
            <Text variant="body" color="danger">
              {extractErrorMessage(certsQuery.error, 'Failed to load certifications.')}
            </Text>
            <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => certsQuery.refetch()} />
          </Card>
        ) : certs.length === 0 ? (
          <Card>
            <Text variant="body" color="secondary">
              No certifications on file.
            </Text>
          </Card>
        ) : (
          <View style={styles.list}>
            {certs.map((cert: CertificationRow) => {
              const expiring = expiringIds.has(cert.id);
              const b = expiring ? { key: 'overdue' as StatusKey, label: 'Expiring' } : certBadge(cert.status);
              const subtitle = [
                cert.certificate_number ? `#${cert.certificate_number}` : null,
                cert.certifying_body,
                cert.expiration_date ? `Expires ${new Date(cert.expiration_date).toLocaleDateString()}` : null,
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <ListRow
                  key={cert.id}
                  title={humanize(cert.certification_type)}
                  subtitle={subtitle || humanize(cert.status)}
                  right={<Badge status={b.key} label={b.label} />}
                />
              );
            })}
          </View>
        )}
      </View>

      {/* ------------------------------------------------------------------ */}
      {/* Contracts */}
      {/* ------------------------------------------------------------------ */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Awarded contracts
        </Text>
        {contractsQuery.isError ? (
          <Card>
            <Text variant="body" color="danger">
              {extractErrorMessage(contractsQuery.error, 'Failed to load contracts.')}
            </Text>
            <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => contractsQuery.refetch()} />
          </Card>
        ) : contracts.length === 0 ? (
          <Card>
            <Text variant="body" color="secondary">
              No awarded contracts.
            </Text>
          </Card>
        ) : (
          <View style={styles.list}>
            {contracts.map((c: ContractRow) => {
              const b = contractBadge(c.status);
              const subtitle = [
                c.contract_number ? `#${c.contract_number}` : null,
                c.awarding_agency,
                c.award_amount ? formatCurrency(c.award_amount) : null,
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <ListRow
                  key={c.id}
                  title={c.awarding_agency || c.contract_number || c.id.slice(0, 8).toUpperCase()}
                  subtitle={subtitle || humanize(c.status)}
                  right={<Badge status={b.key} label={b.label} />}
                />
              );
            })}
          </View>
        )}
      </View>

      {/* ------------------------------------------------------------------ */}
      {/* Compliance */}
      {/* ------------------------------------------------------------------ */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Compliance requirements
        </Text>
        {complianceQuery.isError ? (
          <Card>
            <Text variant="body" color="danger">
              {extractErrorMessage(complianceQuery.error, 'Failed to load compliance requirements.')}
            </Text>
            <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => complianceQuery.refetch()} />
          </Card>
        ) : compliance.length === 0 ? (
          <Card>
            <Text variant="body" color="secondary">
              No compliance requirements tracked.
            </Text>
          </Card>
        ) : (
          <View style={styles.list}>
            {compliance.map((req: ComplianceRequirementRow) => {
              const b = complianceBadge(req.status ?? 'open');
              const subtitle = [
                req.due_date ? `Due ${new Date(req.due_date).toLocaleDateString()}` : null,
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <ListRow
                  key={req.id}
                  title={req.requirement}
                  subtitle={subtitle || humanize(req.status ?? 'open')}
                  right={<Badge status={b.key} label={b.label} />}
                />
              );
            })}
          </View>
        )}
      </View>

      {/* ------------------------------------------------------------------ */}
      {/* Proposals */}
      {/* ------------------------------------------------------------------ */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Proposals
        </Text>
        {proposalsQuery.isError ? (
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(proposalsQuery.error, 'Failed to load proposals.')}
            </Text>
            <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => proposalsQuery.refetch()} />
          </Card>
        ) : proposals.length === 0 ? (
          <Card>
            <Text variant="body" color="secondary">
              No proposals yet.
            </Text>
          </Card>
        ) : (
          <View style={styles.list}>
            {proposals.map((p: ProposalRow) => {
              const b = proposalBadge(p.status);
              const subtitle = [
                p.submitted_at ? `Submitted ${new Date(p.submitted_at).toLocaleDateString()}` : 'Not submitted',
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <ListRow
                  key={p.id}
                  title={`Proposal v${p.version}`}
                  subtitle={subtitle || humanize(p.status)}
                  right={<Badge status={b.key} label={b.label} />}
                  onPress={() => router.push(`/(app)/government/proposal/${p.id}`)}
                  accessibilityLabel={`Open proposal v${p.version}`}
                />
              );
            })}
          </View>
        )}
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
    paddingHorizontal: theme.spacing['2xl'],
  },
  section: { gap: theme.spacing.sm },
  list: { gap: theme.spacing.sm },
});
